import { isNil } from 'lodash';
import { MultiValueProps, SingleValueProps } from 'react-select';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import {
  FilterOption,
  FilterSelectionState,
  FilterTagOption,
  SetOptionFn,
  isFilterTagSelectionList,
  tagComponentOptionToFilterTagOption,
  tagOptionToTagSelection,
  tagSelectionToTagOption,
} from '@shared/components/filter/types';
import { Pill } from '@shared/components/pill';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { OpenView } from './multiSelect/MultiSelect';
import { OptionProps, Option as SelectOption } from './multiSelect/Option';
import { Selection } from './multiSelect/Selection';

function Option({ children: _children, ...props }: OptionProps) {
  const data = props.data;
  const isEmptyFilter = data.value === 'empty';

  return (
    <SelectOption {...props} icon="tag">
      {isEmptyFilter && (
        <Text size="body1" style={{ marginLeft: grid(1) }}>
          {data.label ?? ''}
        </Text>
      )}
      {!isEmptyFilter && (
        <Pill
          backgroundColor={data.archived ? undefined : data.backgroundColor}
          round
          sections={[data.label ?? '']}
          maxWidth={215}
          italic={data.archived}
        />
      )}
    </SelectOption>
  );
}

function MultiValue({ children: _children, ...props }: MultiValueProps<FilterTagOption>) {
  const data = props.data;

  return (
    <Selection {...props}>
      <Pill
        backgroundColor={data.archived ? undefined : data.backgroundColor}
        round
        sections={[data.label ?? i18n('card.actions.groups.missingTagLabel')]}
        maxWidth={grid(24)}
        italic={data.archived || isNil(data.label)}
        onRemove={() =>
          props.setValue(
            [...props.getValue().filter(v => v.value !== data.value)],
            'deselect-option',
          )
        }
      />
    </Selection>
  );
}

function SingleValue({ children: _children, ...props }: SingleValueProps<FilterTagOption>) {
  const data = props.data;

  return (
    <View style={{ alignItems: 'flex-start' }}>
      <Pill
        backgroundColor={data.archived ? undefined : data.backgroundColor}
        round
        sections={[data.label ?? i18n('card.actions.groups.missingTagLabel')]}
        maxWidth={grid(24)}
        italic={data.archived || isNil(data.label)}
        onRemove={() =>
          props.setValue(
            [...props.getValue().filter(v => v.value !== data.value)],
            'deselect-option',
          )
        }
      />
    </View>
  );
}

interface TagOptionsProps {
  filter: FilterOption;
  setOption: SetOptionFn;
  selectedOptions: Map<string, FilterSelectionState>;
  includeEmptyOption: boolean;
  isMultiSelect?: boolean;
}

export function TagOptions({
  filter,
  setOption,
  selectedOptions: selectedOptionsMap,
  includeEmptyOption,
  isMultiSelect = true,
}: TagOptionsProps) {
  const fullFilter = selectedOptionsMap.get(filter.uuid ?? '');
  const negate = fullFilter?.negate;

  const onChange = (selected: FilterTagOption[]) => {
    const selectedArray = isMultiSelect ? selected : selected.slice(0, 1);
    setOption(
      filter,
      'updateSelections',
      selectedArray.map(option => tagOptionToTagSelection(option, filter)),
      negate,
    );
  };

  if (!filter.component || !isTagComponent(filter.component)) {
    return null;
  }

  const tagComponent = filter.component;
  const rawSelections = filter.uuid ? selectedOptionsMap.get(filter.uuid)?.selection ?? [] : [];

  const selectionArray = isFilterTagSelectionList(rawSelections) ? rawSelections : [];
  const selectedOptions: FilterTagOption[] = selectionArray.map(tagSelectionToTagOption);

  const allOptions = tagComponent.options ?? [];
  const tagOptions: FilterTagOption[] = [
    ...allOptions.filter(o => !o.archived),
    ...allOptions.filter(o => o.archived),
  ].map(tagComponentOptionToFilterTagOption);
  const emptyOption: FilterTagOption = {
    value: 'empty',
    label: i18n('card.actions.filters.emptyLabel', { label: filter.label ?? '' }),
  };

  return (
    <OpenView
      componentId={filter.id}
      options={includeEmptyOption ? [emptyOption, ...tagOptions] : tagOptions}
      onChange={onChange}
      selectedOptions={selectedOptions}
      components={{ Option, MultiValue, SingleValue }}
      isMultiSelect={isMultiSelect}
    />
  );
}

// --- Helper functions ------------------------------------------------------
